import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Paper,
} from "@mui/material";
import {
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    Share as ShareIcon,
} from "@mui/icons-material";
import QRCode from "qrcode";

interface QRCodeModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    url: string;
    description?: string;
}

const QRCodeModal: React.FC<QRCodeModalProps> = ({
    open,
    onClose,
    title,
    url,
    description,
}) => {
    const [qrCodeDataURL, setQrCodeDataURL] = useState<string>("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);

    useEffect(() => {
        if (open && url) {
            generateQRCode();
        }
    }, [open, url]);

    const generateQRCode = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const qrDataURL = await QRCode.toDataURL(url, {
                width: 256,
                margin: 2,
                color: {
                    dark: "#000000",
                    light: "#FFFFFF",
                },
                errorCorrectionLevel: "M",
            });

            setQrCodeDataURL(qrDataURL);
        } catch (err) {
            console.error("Error generating QR code:", err);
            setError("Failed to generate QR code");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error("Failed to copy URL:", err);
        }
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    text: description || "Scan this QR code to view documents",
                    url: url,
                });
            } catch (err) {
                console.error("Error sharing:", err);
            }
        } else {
            // Fallback to copy URL
            handleCopyUrl();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6">{title}</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {description}
                    </Typography>
                )}

                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : (
                    <Box sx={{ textAlign: "center" }}>
                        {/* QR Code */}
                        <Paper
                            elevation={2}
                            sx={{
                                display: "inline-block",
                                p: 2,
                                mb: 3,
                                borderRadius: 2,
                            }}
                        >
                            <img
                                src={qrCodeDataURL}
                                alt="QR Code"
                                style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                    display: "block",
                                }}
                            />
                        </Paper>

                        {/* URL Display */}
                        <Paper
                            sx={{
                                p: 2,
                                bgcolor: "grey.50",
                                borderRadius: 1,
                                mb: 2,
                            }}
                        >
                            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                {url}
                            </Typography>
                        </Paper>

                        {/* Action Buttons */}
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            <Button
                                variant="outlined"
                                startIcon={<CopyIcon />}
                                onClick={handleCopyUrl}
                                color={copySuccess ? "success" : "primary"}
                            >
                                {copySuccess ? "Copied!" : "Copy URL"}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ShareIcon />}
                                onClick={handleShare}
                            >
                                Share
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Instructions */}
                <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                        📱 <strong>How to use:</strong> Open your camera app and point it at the QR code,
                        or use any QR code scanner app to access the documents.
                    </Typography>
                </Alert>

                <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        🔐 <strong>Security:</strong> You will need to enter the vehicle's SSN to access the permit documents.
                    </Typography>
                </Alert>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default QRCodeModal;

